<template>
  <div
    class="register-dialog"
    role="dialog"
    aria-modal="true"
    :aria-label="$t('Create account')"
  >
    <div class="register-dialog__overlay" @click="closeModal"></div>

    <div class="register-dialog__glow"></div>

    <div class="register-dialog__viewport">
      <section class="register-dialog__card">
        <button
          type="button"
          class="register-dialog__close"
          :aria-label="$t('Close')"
          @click="closeModal"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M6 6L18 18"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="square"
            />

            <path
              d="M18 6L6 18"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="square"
            />
          </svg>
        </button>

        <header class="register-dialog__header">
          <h2 class="register-dialog__title">
            {{ $t('Создайте аккаунт') }}
          </h2>

          <p class="register-dialog__description">
            {{
              $t(
                'Зарегистрируйтесь, чтобы совершать покупки и управлять аккаунтом BloxNest.',
              )
            }}
          </p>
        </header>

        <div class="register-tabs">
          <button
            type="button"
            class="register-tabs__button"
            @click="openSignIn"
          >
            {{ $t('Вход') }}
          </button>

          <button
            type="button"
            class="register-tabs__button register-tabs__button_active"
          >
            {{ $t('Регистрация') }}
          </button>
        </div>

        <form class="register-form" @submit.prevent="submitRegister">
          <div class="register-form__grid">
            <label class="register-field">
              <span class="register-field__label">
                {{ $t('Имя') }}
              </span>

              <input
                v-model="name"
                type="text"
                class="register-field__input"
                :placeholder="$t('Имя')"
                autocomplete="given-name"
                maxlength="50"
                @input="filterName($event, 'name')"
              />
            </label>

            <label class="register-field">
              <span class="register-field__label">
                {{ $t('Фамилия') }}
              </span>

              <input
                v-model="surname"
                type="text"
                class="register-field__input"
                :placeholder="$t('Фамилия')"
                autocomplete="family-name"
                maxlength="50"
                @input="filterName($event, 'surname')"
              />
            </label>
          </div>

          <label class="register-field">
            <span class="register-field__label">
              {{ $t('Email') }}
            </span>

            <input
              v-model.trim="email"
              type="email"
              class="register-field__input"
              :placeholder="$t('you@mail.com')"
              autocomplete="email"
              maxlength="120"
              @input="filterEmail"
            />
          </label>

          <label class="register-field">
            <span class="register-field__label">
              {{ $t('Пароль') }}
            </span>

            <div class="register-field__control">
              <input
                v-model="pass"
                :type="passwordVisible ? 'text' : 'password'"
                class="register-field__input"
                :placeholder="$t('Пароль')"
                autocomplete="new-password"
              />

              <button
                type="button"
                class="register-field__toggle"
                :aria-label="
                  $t(passwordVisible ? 'Скрыть пароль' : 'Показать пароль')
                "
                @click="passwordVisible = !passwordVisible"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M2 12C4.4 7.8 7.7 5.7 12 5.7C16.3 5.7 19.6 7.8 22 12C19.6 16.2 16.3 18.3 12 18.3C7.7 18.3 4.4 16.2 2 12Z"
                    stroke="currentColor"
                    stroke-width="1.7"
                    stroke-linejoin="round"
                  />

                  <circle
                    cx="12"
                    cy="12"
                    r="3"
                    stroke="currentColor"
                    stroke-width="1.7"
                  />
                </svg>
              </button>
            </div>
          </label>

          <transition name="register-requirements">
            <div v-if="pass.length > 0" class="register-requirements">
              <span
                :class="{
                  'register-requirements__item_met': pass.length >= 8,
                }"
                class="register-requirements__item"
              >
                {{ $t('Не менее 8 символов') }}
              </span>

              <span
                :class="{
                  'register-requirements__item_met': hasPasswordLetter,
                }"
                class="register-requirements__item"
              >
                {{ $t('Минимум одна буква') }}
              </span>

              <span
                :class="{
                  'register-requirements__item_met': hasPasswordNumber,
                }"
                class="register-requirements__item"
              >
                {{ $t('Минимум одна цифра') }}
              </span>
            </div>
          </transition>

          <label class="register-field">
            <span class="register-field__label">
              {{ $t('Подтвердите пароль') }}
            </span>

            <div class="register-field__control">
              <input
                v-model="passConfirm"
                :type="confirmPasswordVisible ? 'text' : 'password'"
                :class="[
                  'register-field__input',
                  {
                    'register-field__input_error': passwordMismatch,
                  },
                ]"
                :placeholder="$t('Подтвердите пароль')"
                autocomplete="new-password"
              />

              <button
                type="button"
                class="register-field__toggle"
                :aria-label="
                  $t(
                    confirmPasswordVisible
                      ? 'Скрыть пароль'
                      : 'Показать пароль',
                  )
                "
                @click="confirmPasswordVisible = !confirmPasswordVisible"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M2 12C4.4 7.8 7.7 5.7 12 5.7C16.3 5.7 19.6 7.8 22 12C19.6 16.2 16.3 18.3 12 18.3C7.7 18.3 4.4 16.2 2 12Z"
                    stroke="currentColor"
                    stroke-width="1.7"
                    stroke-linejoin="round"
                  />

                  <circle
                    cx="12"
                    cy="12"
                    r="3"
                    stroke="currentColor"
                    stroke-width="1.7"
                  />
                </svg>
              </button>
            </div>

            <span v-if="passwordMismatch" class="register-field__error">
              {{ $t('Пароли не совпадают') }}
            </span>
          </label>

          <div class="register-checkbox">
            <label class="register-checkbox__control">
              <input v-model="terms" type="checkbox" name="terms" />

              <span class="register-checkbox__box">
                <svg
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M3 8.5L6.3 11.5L13 4.5"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </span>
            </label>

            <span class="register-checkbox__text">
              {{ $t('Я согласен с') }}

              <button
                v-if="termsPage"
                type="button"
                class="register-checkbox__link"
                @click.stop="openLegalPage(termsPage)"
              >
                {{ termsPage.title }}
              </button>

              <template v-if="termsPage && privacyPage">
                {{ $t('и') }}
              </template>

              <button
                v-if="privacyPage"
                type="button"
                class="register-checkbox__link"
                @click.stop="openLegalPage(privacyPage)"
              >
                {{ privacyPage.title }}
              </button>
            </span>
          </div>

          <Captcha v-model="captcha" />

          <button
            type="submit"
            class="button shadowed register-form__submit"
            :disabled="!requiredRegisterFieldsAreFilled || loading"
          >
            <span>{{ $t('СОЗДАТЬ АККАУНТ') }}</span>
          </button>
        </form>

        <transition name="register-message">
          <div
            v-if="$parent.error"
            class="register-message register-message_error"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M12 8V13"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
              />

              <path
                d="M12 17H12.01"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
              />

              <path
                d="M10.3 3.6L2.4 17.2C1.6 18.6 2.6 20.3 4.2 20.3H19.8C21.4 20.3 22.4 18.6 21.6 17.2L13.7 3.6C12.9 2.2 11.1 2.2 10.3 3.6Z"
                stroke="currentColor"
                stroke-width="2"
                stroke-linejoin="round"
              />
            </svg>

            <span>{{ $parent.error }}</span>
          </div>
        </transition>

        <p class="register-dialog__footer">
          {{
            $t('Защищено reCAPTCHA. Продолжая, вы принимаете правила сервиса.')
          }}
        </p>
      </section>
    </div>
  </div>
</template>

<script>
import Captcha from './Captcha.vue'

export default {
  name: 'Registration',

  components: { Captcha },

  props: {
    loading: {
      type: Boolean,
      default: false,
    },

    textPageList: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      email: '',
      pass: '',
      name: '',
      surname: '',
      passConfirm: '',
      terms: false,
      captcha: '',

      passwordVisible: false,
      confirmPasswordVisible: false,
    }
  },

  computed: {
    termsPage() {
      return (
        this.textPageList.find((item) => {
          return Number(item?.is_terms) === 1
        }) || null
      )
    },

    privacyPage() {
      return (
        this.textPageList.find((item) => {
          return Number(item?.is_privacy) === 1
        }) || null
      )
    },

    hasPasswordLetter() {
      return /[A-Za-zА-Яа-яЁё]/.test(this.pass)
    },

    hasPasswordNumber() {
      return /\d/.test(this.pass)
    },

    passwordMismatch() {
      return Boolean(this.passConfirm && this.pass !== this.passConfirm)
    },

    passwordIsValid() {
      return (
        this.pass.length >= 8 &&
        this.hasPasswordLetter &&
        this.hasPasswordNumber &&
        this.pass === this.passConfirm
      )
    },

    requiredRegisterFieldsAreFilled() {
      return Boolean(
        this.name.trim().length >= 2 &&
          this.surname.trim().length >= 2 &&
          this.isValidEmail(this.email) &&
          this.passwordIsValid &&
          this.terms &&
          this.captcha,
      )
    },
  },

  mounted() {
    window.addEventListener('keydown', this.handleKeydown)
  },

  beforeDestroy() {
    window.removeEventListener('keydown', this.handleKeydown)
  },

  methods: {
    openLegalPage(page) {
      if (!page) {
        return
      }

      if (this.$parent && typeof this.$parent.goToPage === 'function') {
        this.$parent.goToPage(page)
      }
    },
    handleKeydown(event) {
      if (event.key === 'Escape' && !this.loading) {
        this.closeModal()
      }
    },

    closeModal() {
      if (this.loading) {
        return
      }

      if (this.$parent && typeof this.$parent.closeSignUp === 'function') {
        this.$parent.closeSignUp()
      }
    },

    openSignIn() {
      if (this.loading) {
        return
      }

      if (this.$parent && typeof this.$parent.clearError === 'function') {
        this.$parent.clearError()
      }

      if (this.$parent && typeof this.$parent.openSignIn === 'function') {
        this.$parent.openSignIn()
      }
    },

    filterName(event, field) {
      let value = event.target.value

      value = value
        .replace(/[^a-zA-Zа-яА-ЯёЁ\s'-]/g, '')
        .replace(/\s+/g, ' ')
        .trimStart()
        .slice(0, 50)

      this[field] = value
    },

    filterEmail(event) {
      this.email = event.target.value
        .replace(/[^a-zA-Z0-9@._%+-]/g, '')
        .replace(/\s+/g, '')
        .slice(0, 120)
    },

    isValidEmail(value) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim())
    },

    submitRegister() {
      if (!this.requiredRegisterFieldsAreFilled || this.loading) {
        return
      }

      if (this.$parent && typeof this.$parent.clearError === 'function') {
        this.$parent.clearError()
      }

      this.$emit('registration', {
        name: this.name.trim(),
        surname: this.surname.trim(),
        email: this.email.trim(),
        password: this.pass,
        passConfirm: this.passConfirm,
        altcha: this.captcha,
      })
    },
  },
}
</script>

<style scoped lang="scss">
.register-dialog {
  position: fixed;
  inset: 0;
  z-index: 20;

  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;

  overflow-y: auto;
}

.register-dialog__overlay {
  position: fixed;
  inset: 0;

  background: radial-gradient(
      circle at 50% -10%,
      rgba(33, 193, 126, 0.09),
      transparent 43%
    ),
    rgba(4, 7, 6, 0.9);

  backdrop-filter: blur(8px);
  cursor: pointer;
}

.register-dialog__glow {
  position: fixed;
  top: -360px;
  left: 50%;

  width: 900px;
  height: 610px;

  transform: translateX(-50%);

  background: radial-gradient(
    circle,
    rgba(35, 218, 139, 0.31) 0%,
    rgba(33, 193, 126, 0.1) 38%,
    transparent 72%
  );

  filter: blur(20px);
  pointer-events: none;
}

.register-dialog__viewport {
  position: relative;
  z-index: 1;

  width: 100%;
  max-width: 500px;
  margin: auto;
}

.register-dialog__card {
  position: relative;
  width: 100%;
  padding: 38px;

  background: linear-gradient(
      160deg,
      rgba(33, 193, 126, 0.045),
      transparent 48%
    ),
    var(--bunker-86);

  border: 1px solid var(--outer-space);
  border-radius: 13px;

  box-shadow: 0 42px 105px rgba(0, 0, 0, 0.65),
    0 0 70px rgba(33, 193, 126, 0.055),
    inset 2px 2px 0 rgba(255, 255, 255, 0.02),
    inset -2px -2px 0 rgba(0, 0, 0, 0.22);
}

.register-dialog__close {
  position: absolute;
  top: 15px;
  right: 15px;
  z-index: 3;

  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;

  color: var(--pewter);
  background: var(--heavy-metal);
  border: 1px solid var(--outer-space);
  border-radius: 8px;

  transition: color 0.2s ease, background-color 0.2s ease,
    border-color 0.2s ease, transform 0.2s ease;

  svg {
    width: 15px;
    height: 15px;
  }

  @media (any-hover: hover) {
    &:hover {
      color: var(--gray-nurse);
      background: var(--outer-space);
      border-color: rgba(33, 193, 126, 0.35);
      transform: rotate(90deg);
    }
  }
}

.register-dialog__header {
  padding: 0 30px;
  margin-bottom: 26px;
  text-align: center;
}

.register-dialog__title {
  margin: 0;

  font-family: var(--font-2);
  font-size: 27px;
  font-weight: 700;
  line-height: 35px;

  color: var(--gray-nurse);
}

.register-dialog__description {
  margin: 7px auto 0;

  font-family: var(--font-1);
  font-size: 12px;
  line-height: 19px;

  color: var(--mantle);
}

.register-tabs {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 4px;

  padding: 4px;
  margin-bottom: 25px;

  background: rgba(7, 13, 10, 0.72);
  border: 1px solid var(--outer-space);
  border-radius: 9px;
}

.register-tabs__button {
  min-height: 39px;
  padding: 9px 12px;

  font-family: var(--font-1);
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;

  color: var(--mantle);
  background: transparent;
  border: 0;
  border-radius: 7px;

  transition: color 0.2s ease, background-color 0.2s ease;

  @media (any-hover: hover) {
    &:hover {
      color: var(--gray-nurse);
    }
  }

  &_active {
    color: var(--racing-green);

    background: linear-gradient(142deg, var(--44), var(--31));

    box-shadow: inset 0 -3px 0 rgba(0, 0, 0, 0.22),
      inset 0 2px 0 rgba(255, 255, 255, 0.15);
  }
}

.register-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.register-form__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.register-field {
  display: block;
  margin: 0;
  padding: 0;
}

.register-field__label {
  display: block;
  margin-bottom: 7px;

  font-family: var(--font-1);
  font-size: 12px;
  font-weight: 500;
  line-height: 17px;

  color: var(--pewter);
}

.register-field__control {
  position: relative;
}

.register-field__input {
  display: block;
  width: 100%;
  min-height: 46px;
  margin: 0;
  padding: 11px 14px;

  font-family: var(--font-1);
  font-size: 13px;
  line-height: 21px;

  color: var(--gray-nurse);
  background: rgba(7, 13, 10, 0.74);
  border: 1px solid var(--outer-space);
  border-radius: 9px;
  outline: none;
  appearance: none;

  box-shadow: inset 2px 2px 0 rgba(0, 0, 0, 0.34),
    inset -1px -1px 0 rgba(255, 255, 255, 0.025);

  transition: border-color 0.2s ease, background-color 0.2s ease,
    box-shadow 0.2s ease;

  &::placeholder {
    font-family: var(--font-1);
    font-size: 13px;
    color: var(--mantle);
    opacity: 0.62;
  }

  &:focus {
    background: var(--heavy-metal);
    border-color: var(--31);

    box-shadow: inset 2px 2px 0 rgba(0, 0, 0, 0.3),
      0 0 0 3px rgba(33, 193, 126, 0.1);
  }

  &_error {
    border-color: rgba(255, 71, 102, 0.6);
  }

  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus {
    -webkit-text-fill-color: var(--gray-nurse);
    transition: background-color 9999s ease-in-out 0s;
  }
}

.register-field__control .register-field__input {
  padding-right: 44px;
}

.register-field__toggle {
  position: absolute;
  top: 50%;
  right: 11px;

  width: 27px;
  height: 27px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;

  color: var(--mantle);
  background: transparent;
  border: 0;
  transform: translateY(-50%);

  transition: color 0.2s ease;

  svg {
    width: 17px;
    height: 17px;
  }

  @media (any-hover: hover) {
    &:hover {
      color: var(--mountain-meadow);
    }
  }
}

.register-field__error {
  display: block;
  margin-top: 6px;

  font-family: var(--font-1);
  font-size: 11px;
  line-height: 16px;

  color: #ff8b9e;
}

.register-requirements {
  display: flex;
  flex-wrap: wrap;
  gap: 7px 14px;
  margin-top: -5px;
}

.register-requirements__item {
  position: relative;
  padding-left: 13px;

  font-family: var(--font-1);
  font-size: 10px;
  line-height: 15px;

  color: var(--mantle);

  transition: color 0.2s ease;

  &::before {
    content: '';
    position: absolute;
    top: 6px;
    left: 0;

    width: 4px;
    height: 4px;

    background: var(--mantle);
    border-radius: 50%;

    transition: background-color 0.2s ease;
  }

  &_met {
    color: var(--mountain-meadow);

    &::before {
      background: var(--mountain-meadow);
    }
  }
}

.register-checkbox {
  display: flex;
  align-items: flex-start;

  gap: 11px;

  margin: 2px 0 0;
}

.register-checkbox__control {
  position: relative;

  flex: 0 0 21px;

  width: 21px;
  height: 21px;

  margin: 0;

  cursor: pointer;

  > input {
    position: absolute;

    width: 1px;
    height: 1px;

    opacity: 0;
    pointer-events: none;
  }

  > input:checked + .register-checkbox__box {
    color: var(--racing-green);

    background: linear-gradient(142deg, var(--44), var(--31));

    border-color: var(--31);

    svg {
      opacity: 1;
      transform: scale(1);
    }
  }

  > input:focus-visible + .register-checkbox__box {
    box-shadow: 0 0 0 3px rgba(33, 193, 126, 0.12);
  }
}

.register-checkbox__box {
  width: 21px;
  min-width: 21px;
  height: 21px;

  display: flex;
  align-items: center;
  justify-content: center;

  color: transparent;

  background: rgba(7, 13, 10, 0.74);

  border: 1px solid var(--outer-space);
  border-radius: 5px;

  cursor: pointer;

  svg {
    width: 13px;
    height: 13px;

    opacity: 0;
    transform: scale(0.7);

    transition: opacity 0.2s ease, transform 0.2s ease;
  }
}

.register-checkbox__text {
  flex: 1 1 auto;

  font-family: var(--font-1);
  font-size: 11px;
  line-height: 18px;

  color: var(--mantle);
}

.register-checkbox__link {
  display: inline;

  margin: 0;
  padding: 0;

  font: inherit;
  font-weight: 500;
  line-height: inherit;

  color: var(--mountain-meadow);

  background: transparent;
  border: 0;

  cursor: pointer;

  transition: color 0.2s ease, text-decoration-color 0.2s ease;

  @media (any-hover: hover) {
    &:hover {
      color: var(--gray-nurse);
      text-decoration: underline;
    }
  }
}

.register-form__submit {
  width: 100%;
  min-height: 48px;
  justify-content: center;
  margin-top: 2px;
  padding: 13px 18px;

  font-family: var(--font-3);
  font-size: 11px;
  font-weight: 700;
  line-height: 14px;
  text-transform: uppercase;
}

.register-message {
  display: flex;
  align-items: flex-start;
  gap: 9px;

  margin-top: 17px;
  padding: 11px 13px;

  font-family: var(--font-1);
  font-size: 12px;
  line-height: 18px;

  border-radius: 8px;

  svg {
    flex: 0 0 17px;
    width: 17px;
    height: 17px;
    margin-top: 1px;
  }

  &_error {
    color: #ff8b9e;
    background: rgba(255, 71, 102, 0.08);
    border: 1px solid rgba(255, 71, 102, 0.25);
  }
}

.register-dialog__footer {
  max-width: 350px;
  margin: 20px auto 0;

  font-family: var(--font-1);
  font-size: 10px;
  line-height: 16px;
  text-align: center;

  color: var(--mantle);
  opacity: 0.7;
}

.register-requirements-enter-active,
.register-requirements-leave-active,
.register-message-enter-active,
.register-message-leave-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}

.register-requirements-enter,
.register-requirements-leave-to,
.register-message-enter,
.register-message-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}

@media (max-width: 767.98px) {
  .register-dialog {
    align-items: flex-start;
    padding: 18px 16px;
  }

  .register-dialog__viewport {
    max-width: 480px;
    padding: 20px 0;
  }

  .register-dialog__card {
    padding: 31px 24px 27px;
  }

  .register-dialog__title {
    font-size: 24px;
    line-height: 31px;
  }
}

@media (max-width: 540px) {
  .register-dialog {
    padding: 0;
  }

  .register-dialog__viewport {
    min-height: 100%;
    max-width: none;
    display: flex;
    align-items: center;
    padding: 12px;
  }

  .register-dialog__card {
    padding: 29px 18px 23px;
    border-radius: 10px;
  }

  .register-dialog__close {
    top: 12px;
    right: 12px;
  }

  .register-dialog__header {
    padding: 0 28px;
    margin-bottom: 23px;
  }

  .register-form__grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .register-tabs {
    margin-bottom: 21px;
  }

  .register-field__input {
    min-height: 44px;
  }

  .register-requirements {
    flex-direction: column;
    gap: 5px;
  }
}
</style>
